import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface PredictionDisplayProps {
  selectedTarget: string | null;
  selectedDrug: string | null;
  apiBaseUrl: string;
  apiHeaders: Record<string, string>;
  onCreditsUpdate: () => void;
}

interface DisplayScoreProps {
  value: number;
  confidenceInterval: number;
  confidenceLevel: number;
}

const DisplayScore: React.FC<DisplayScoreProps> = ({ value, confidenceInterval, confidenceLevel }) => {
  return (
    <div className="flex items-baseline font-space-grotesk text-4xl">
      <span className="mr-2">pKd</span>
      <div className="relative inline-flex items-center">
        <span className="text-sm absolute -top-2 w-full text-center">
          {confidenceLevel}%
        </span>
        <span className="mx-2">=</span>
      </div>
      <span className="mx-2">{value}</span>
      <span className="mx-2">±</span>
      <span>{confidenceInterval}</span>
    </div>
  );
};

const PredictionDisplay: React.FC<PredictionDisplayProps> = ({ 
  selectedTarget, 
  selectedDrug, 
  apiBaseUrl,
  apiHeaders,
  onCreditsUpdate
}) => {
  const [predictionState, setPredictionState] = useState<{
      isLoading: boolean;
      error: string | null;
      result: any | null;
      taskId: string | null;
    }>({
      isLoading: false,
      error: null,
      result: null,
      taskId: null
    });

  // Poll the prediction status at regular intervals
  useEffect(() => {
    let intervalId: number | null = null;
    
    if (predictionState.taskId) {
      intervalId = window.setInterval(async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/dta/predict/${predictionState.taskId}`,
            { headers: apiHeaders }
          );
          
          const data = response.data;
          
          if (data.status === 'SUCCESS') {
            // Extract result from the status response
            setPredictionState(prev => ({
              ...prev,
              isLoading: false,
              result: data.result,
              taskId: null
            }));
            
            // Update credits after successful prediction
            onCreditsUpdate();
            
            // Clear the interval when we get results
            if (intervalId) clearInterval(intervalId);
          } else if (data.status === 'FAILURE') {
            setPredictionState(prev => ({
              ...prev,
              isLoading: false,
              error: data.message || 'Prediction failed',
              taskId: null
            }));
            
            // Update credits even on failure as they might have been deducted
            onCreditsUpdate();
            
            // Clear the interval when task fails
            if (intervalId) clearInterval(intervalId);
          }
        } catch (error: any) {
          console.error('Error polling prediction status:', error);
          
          if (error.response?.status === 401 || error.response?.status === 403) {
            setPredictionState(prev => ({
              ...prev,
              isLoading: false,
              error: 'Authentication error. Please login again.',
              taskId: null
            }));
            
            // Clear interval on auth errors
            if (intervalId) clearInterval(intervalId);
          }
          // For other errors, continue polling
        }
      }, 1500); // Poll every 1.5 seconds
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [predictionState.taskId, apiBaseUrl, apiHeaders, onCreditsUpdate]);

  useEffect(() => {
    const fetchPrediction = async () => {
      // Only proceed if both selections are present
      if (!selectedTarget || !selectedDrug) {
        setPredictionState(prev => ({ ...prev, result: null, error: null }));
        return;
      }

      setPredictionState(prev => ({ 
        ...prev, 
        isLoading: true, 
        error: null, 
        result: null,
        taskId: null
      }));

      try {
        // Submit a new prediction task
        const response = await axios.post(
          `${apiBaseUrl}/dta/predict`,
          {
            protein_id: selectedTarget,
            compound_id: selectedDrug
          },
          {
            headers: {
              'Content-Type': 'application/json',
              ...apiHeaders
            },
          }
        );
        
        const data = response.data;
        
        if (data.task_id) {
          // Store task ID for status polling
          setPredictionState(prev => ({
            ...prev,
            taskId: data.task_id
          }));
        } else if (data.predicted_kd) {
          // Immediate result from cache
          setPredictionState(prev => ({
            ...prev,
            isLoading: false,
            result: data.predicted_kd
          }));
          // Update credits for cached results too
          onCreditsUpdate();
        }
      } catch (error: any) {
        console.error('Error requesting prediction:', error);
        setPredictionState(prev => ({
          ...prev,
          isLoading: false,
          error: error.response?.data?.message || error.message || 'Failed to get prediction',
          result: null
        }));
      }
    };

    fetchPrediction();
  }, [selectedTarget, selectedDrug, apiBaseUrl, apiHeaders, onCreditsUpdate]);

  if (!selectedTarget || !selectedDrug) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        Please select both a target and a drug
      </div>
    );
  }

  if (predictionState.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
        <div className="text-gray-600">Calculating binding affinity...</div>
      </div>
    );
  }

  if (predictionState.error) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="text-red-500 text-center">
          <p className="font-bold">Error:</p>
          <p>{predictionState.error}</p>
        </div>
        <button 
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => setPredictionState(prev => ({ ...prev, error: null }))}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (predictionState.result !== null) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="text-2xl font-bold">Predicted Binding Affinity :</div>
        <div></div>
        <DisplayScore
          value={predictionState.result} 
          confidenceInterval={1.5}
          confidenceLevel={95}
        />
      </div>
    );
  }

  return null;
};

export default PredictionDisplay;